/**
 * Whitelabel i18n Integration Composable
 * Provides whitelabel-aware translations that can override default i18n content
 * Addresses Subtask 7.3: Audit and Update Components for Whitelabel Opportunities
 */

import { computed, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useWhitelabelBranding } from './useWhitelabelConfig'

export interface UseWhitelabelI18nReturn {
  // Whitelabel-aware translations
  appTitle: ComputedRef<string>
  getWhitelabelText: (key: string, fallbackKey?: string) => string
  isUsingWhitelabelOverride: (key: string) => boolean
  
  // Debugging and validation
  getAvailableOverrides: () => string[]
  validateOverrides: () => { valid: string[], invalid: string[] }
}

/**
 * Composable for whitelabel-aware i18n integration
 * Allows certain translations to be overridden by whitelabel configuration
 */
export function useWhitelabelI18n(): UseWhitelabelI18nReturn {
  const { t } = useI18n()
  const whitelabelBranding = useWhitelabelBranding()
  
  // App title that uses whitelabel app name instead of i18n default
  const appTitle = computed(() => {
    // Use whitelabel app name instead of hardcoded i18n title
    return whitelabelBranding.appName || t('app.title')
  })
  
  /**
   * Get whitelabel-aware text with fallback to i18n
   * Allows specific keys to be overridden by whitelabel configuration
   */
  function getWhitelabelText(key: string, fallbackKey?: string): string {
    // Handle specific whitelabel overrides
    switch (key) {
      case 'app.title':
        return appTitle.value
      default:
        // For all other keys, use standard i18n
        return t(fallbackKey || key)
    }
  }
  
  /**
   * Check if a key is using whitelabel override vs i18n default
   */
  function isUsingWhitelabelOverride(key: string): boolean {
    switch (key) {
      case 'app.title':
        return whitelabelBranding.appName !== 'Rewards App' // Not using fallback
      default:
        return false
    }
  }
  
  /**
   * Get list of available whitelabel text overrides
   */
  function getAvailableOverrides(): string[] {
    return ['app.title']
  }
  
  /**
   * Validate current whitelabel overrides configuration
   */
  function validateOverrides(): { valid: string[], invalid: string[] } {
    const available = getAvailableOverrides()
    const valid: string[] = []
    const invalid: string[] = []
    
    available.forEach(key => {
      if (isUsingWhitelabelOverride(key)) {
        valid.push(key)
      }
    })
    
    return { valid, invalid }
  }
  
  return {
    appTitle,
    getWhitelabelText,
    isUsingWhitelabelOverride,
    getAvailableOverrides,
    validateOverrides
  }
}